/// <reference types="vite/client" />
/**
 * Firebase initialisation — single shared instance for the entire CMS.
 * Import `db`, `auth`, and `storage` from here everywhere in the app.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate required config at startup
const requiredKeys = [
  'apiKey', 'authDomain', 'projectId', 'storageBucket',
  'messagingSenderId', 'appId',
] as const;

for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    throw new Error(
      `Missing Firebase config key: VITE_FIREBASE_${key.toUpperCase().replace(/([A-Z])/g, '_$1')}. ` +
      'Check your .env.local file.'
    );
  }
}

const app = initializeApp(firebaseConfig);

// Firestore — using memory cache during development to avoid stale data issues
export const db = initializeFirestore(app, {});

export const auth      = getAuth(app);
export const storage   = getStorage(app);
export const functions = getFunctions(app);

// Connect to emulators in local development
if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(auth,           'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db,        'localhost', 8080);
  connectStorageEmulator(storage,     'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.info('[Firebase] Connected to emulators');
}

export default app;
